import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useStore } from 'jotai';

import { useEmailApiClient } from '@/api/clients/useEmailApiClient.util.ts';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { returnPromise } from '@/utils/returnPromise.util.ts';
import { folderStateAtom } from '@/views/emails/context/EmailsContext.ts';
import {
  markEmailReadInList,
  scheduleThreadListRefresh,
} from '@/views/emails/util/threadListCache.util.ts';
import { ThreadsInfiniteData } from '@/views/emails/util/useReadEmails.util.tsx';
import { UseReadThreadResponse } from '@/views/emails/util/useReadThread.util.tsx';

type Req = {
  currentBoxId?: string;
  emailId: string;
  threadId: string;
};

type Return = {
  isMarkingEmailRead: boolean;
  markEmailRead: (req: Req) => Promise<void>;
};

export const useMarkEmailAsRead = (): Return => {
  const { emailUpdateApi } = useEmailApiClient();
  const { userId } = useUserContext();
  const queryClient = useQueryClient();
  const store = useStore();

  const { isPending, mutateAsync } = useMutation<void, AxiosError, Req>({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      await emailUpdateApi.markEmailAsRead(userId, req.emailId);
    },
    mutationKey: [QueryKeyEnum.EMAILS.MARK_READ],
    onSuccess: async (_, req) => {
      queryClient.setQueryData<UseReadThreadResponse>(
        [QueryKeyEnum.EMAILS.THREAD, req.threadId],
        cur => {
          if (!cur) {
            return undefined;
          }
          if (cur.threadId !== req.threadId) {
            return cur;
          }
          cur.emails = cur.emails.map(it => {
            if (it.emailId !== req.emailId) {
              return it;
            }
            it.isRead = true;
            return it;
          });
          return cur;
        },
      );

      const listMayStillShowUnread =
        store.get(folderStateAtom).unreadOnly ||
        queryClient.isFetching({ queryKey: [QueryKeyEnum.EMAILS.ALL] }) > 0;
      queryClient.setQueriesData<ThreadsInfiniteData>(
        { queryKey: [QueryKeyEnum.EMAILS.ALL] },
        markEmailReadInList(req.emailId),
      );
      if (listMayStillShowUnread) {
        scheduleThreadListRefresh(queryClient);
      }

      await returnPromise(null, 1000);

      await queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.DASHBOARD.EMAILS],
      });

      await queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.EMAILS.FOLDERS_STATE],
      });
    },
  });

  return {
    isMarkingEmailRead: isPending,
    markEmailRead: mutateAsync,
  };
};
